import React, { ReactElement, useRef, useState } from "react";
import Head from "next/head";
import Hero from "@/components/landing-new/Hero";

import PathToHomeOwnership from "@/components/landing-new/PathToHomeOwnership";
import GotQuestions from "@/components/landing-new/GotQuestions";
import BudgetCalculator from "@/components/landing-new/BudgetCalculator";
import ReadyToMoveBanner from "@/components/landing-new/ReadyToMoveBanner";
import BenefitsOfKeyzyNow from "@/components/landing-new/BenefitsOfKeyzyNow";
import FlatsAndHousesToRent from "@/components/landing-new/FlatsAndHousesToRent";
import getConfig from "next/config";
import { MainLayout } from "@/containers/layout/NowLayout";
import ReferralBanner from "@/components/landing-new/ReferralBanner";
import { FAQContext } from "@/contexts/FAQContext/FAQContext";
import LiveKeyzyDevelopments from "@/components/landing-new/LiveKeyzyDevelopments";

const KeyzyNow = (): JSX.Element => {
  const faqRef = useRef<HTMLDivElement | null>(null);
  const [faqToOpen, setFaqToOpen] = useState<number | null>(null);

  const scrollToFAQ = () => {
    faqRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const openFaq = (index: number) => {
    setFaqToOpen(index);
  };

  const setFaqRef = (el: HTMLDivElement | null) => {
    faqRef.current = el;
  };

  return (
    <>
      <Head>
        <title>Keyzy - Rent to buy a home, today!</title>
        <link rel="canonical" href="https://www.keyzy.com" />
        <meta
          name="description"
          key="description"
          content="Rent a Keyzy home today, buy later at a fixed price, using your rent as your deposit. Start your journey to homeownership now!"
        />
        <meta
          name="twitter:card"
          content="Rent a Keyzy home today, buy later at a fixed price, using your rent as your deposit. Start your journey to homeownership now!"
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta property="og:image" content="/now/meta-og-keyzy-now.png" key="ogimage" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:title" content="Keyzy - Rent to buy a home, today!" key="ogtitle" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Rent a Keyzy home today, buy later at a fixed price, using your rent as your deposit. Start your journey to homeownership now!"
          key="ogdesc"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Keyzy",
              url: "https://www.keyzy.com",
              logo: "https://www.keyzy.com/keyzy-logo.png",
              description:
                "Keyzy is a rent-to-buy platform that lets you rent a home in London today, build a deposit through your rent, and buy the home later at a locked-in price.",
              sameAs: ["https://twitter.com/keyzyhomes"],
              areaServed: {
                "@type": "City",
                name: "London",
                containsPlace: [
                  { "@type": "Place", name: "Southall" },
                  { "@type": "Place", name: "Hayes" },
                  { "@type": "Place", name: "Harrow" },
                  { "@type": "Place", name: "Ealing" },
                  { "@type": "Place", name: "Clapham" },
                  { "@type": "Place", name: "Bermondsey" },
                  { "@type": "Place", name: "Hanwell" },
                  { "@type": "Place", name: "Wandsworth" },
                ],
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "Do I need a deposit?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "You don't need a deposit to move into a Keyzy home! There is a one-off capital payment made on the grant of your contractual right to buy your home — this is your Future Purchase Agreement. It costs £499 for 1 applicant, £999 for 2, or £1,499 for 3 or more. You will need a deposit to buy the house at the end of the lease if you want to. We can help guide you towards saving a regular amount each month as well as return all of your rent at the end of 2 years. You should aim for at least a 10% deposit to buy a home to stand the most chance of being approved and getting a good deal.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Are there any hidden fees?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "No. There is a one-off capital payment made on the grant of your contractual right to buy your home — this is your Future Purchase Agreement. It costs £499 for 1 applicant, £999 for 2, or £1,499 for 3 or more. After that, the only thing you pay to us is your monthly rent. Your rental will be fixed for the full duration of your lease, no increases! There are no fees if you choose to walk away at the end of the lease. Buying the home at the end will incur legal fees from the solicitor you choose, stamp duty, and potentially mortgage fees.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What are the basic eligibility criteria?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "You need to be over 18 and have the right to live in the UK. Your combined household income must be at least £80,000 (this is because of the current cheapest apartment available and is subject to change). You must not have entered an IVA or bankruptcy in the past three years, have any CCJs or defaults in the past 12 months, or have missed any payments in the past six months. Keyzy will verify your income via Open Banking and your ID digitally.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What if I don't want to buy later?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "You're free to leave the home at any point in your rental period as long as you give 2 months' notice. You do not get your rent returned if you choose not to buy — it will be as if you were just renting normally. In some cases Keyzy can sign another lease, but there is no guarantee.",
                  },
                },
                {
                  "@type": "Question",
                  name: "How is Keyzy able to give back all my rent and still make money?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Keyzy is a rent-to-own company. We exist to sell homes to first time buyers and help people onto the property ladder. We’ve got no interest in being long-term landlords. We make money by selling the home to you at the end of your 2-year tenancy. We work with investors and developers to make lots of new-build homes available on our rent-to-own product. We believe in this model because we know there are loads of people out there who want to buy a home but need a little help to get ready before they do. There is also a one-off capital payment made on the grant of your contractual right to buy your home — this is your Future Purchase Agreement. It costs £499 for 1 applicant, £999 for 2, or £1,499 for 3 or more. After that, the only thing you pay to us is your monthly rent.",
                  },
                },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://keyzy.com/" },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "Keyzy",
              url: "https://keyzy.com/",
            }),
          }}
        />
      </Head>
      <div className="bg-white">
        <FAQContext.Provider value={{ scrollToFAQ, openFaq, faqToOpen, setFaqRef }}>
          <Hero />
          {/* <BudgetCalculator /> */}
          <LiveKeyzyDevelopments />
          <FlatsAndHousesToRent />
          <BenefitsOfKeyzyNow />
          <div className="relative h-full bg-white w-full overflow-hidden mb-9 md:mb-0">
            <div className="flex w-full left-0 right-0 m overflow-hidden bg-repeat-x lg:mt-7 h-24 md:h-52 xl:h-48 bg-[url('/intermediaries/city-outline-mobile.svg')]  md:bg-[url('/intermediaries/city-outline-tablet.svg')] xl:bg-[url('/intermediaries/city-outline-desktop.svg')]" />
          </div>
          <PathToHomeOwnership />
          <ReadyToMoveBanner />
          <GotQuestions />
        </FAQContext.Provider>

        <div className="relative h-full bg-white w-full overflow-hidden">
          <ReferralBanner />
        </div>
      </div>
    </>
  );
};

KeyzyNow.getLayout = function getLayout(page: ReactElement) {
  return (
    <>
      <MainLayout theme="dark">{page}</MainLayout>
    </>
  );
};

export default KeyzyNow;

export async function getServerSideProps() {
  return { props: {} };
}
